/* **The INK primary — a filled, neutral, un-accented call to action.**
 *
 * The sibling of `emberButton.module.css`, and it exists because the client-detail canon draws its
 * primaries in ink rather than in ember: `Skomentuj sesję` and `Potwierdź` on view 04,
 * `Zapisz komentarz` on view 05, `Zapisz pomiar` on view 08. All four measure rgb(17, 17, 19) —
 * `--text` — with white ink on top.
 *
 * That is the accent-as-scalpel rule doing its job rather than an omission. A screen with three
 * ember buttons has no accent left: on view 04 the ember is spent on ONE thing, the selected tab's
 * link out of the plan card, and on view 07 it is spent on the tonnage line. The primaries beside
 * them are ink so that the one orange mark on the screen still means something.
 *
 * ── How a surface uses it, and it is `emberButton`'s contract exactly ────────────────────────
 * Two classes on the element: this one for the TREATMENT — fill, ink, border, transition and the
 * three states — and the surface's own for the GEOMETRY, declared there as ORDINARY PROPERTIES:
 *
 *   height          padding         border-radius
 *   font-size       font-weight     letter-spacing
 *
 * **Nothing may be declared on both sides.** A declaration living in both a shared module and a
 * page's own is settled by CHUNK ORDER, which differs between the dev server and the build — that
 * cost a focus ring that was neutral in development and accent in the shipped bundle, measured on
 * 5 August 2026 and written up at length in `emberButton.module.css`. The same trap applies here.
 *
 * ── Why not just `emberButton` with a variant class ──────────────────────────────────────────
 * A variant would mean every surface writes two class names to get the ordinary case and three to
 * get the accent one, and the file would carry a fill it does not always use. More to the point,
 * the two are different DECISIONS rather than two skins: reaching for `.ember` is a claim that this
 * is the one real action on the screen, and reaching for `.ink` is a claim that it is not. Making
 * them one control with a flag would let that claim be changed by a prop.
 *
 * Flat fill, hairline edge, no shadow, no glow, nothing moves in Z — the same three rules, and the
 * ban on a coloured blur is absolute regardless of the fill being neutral. */

.ink {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  white-space: nowrap;
  color: var(--on-ink);
  background: var(--text);

  /* Split into three, like the ember's: the value allowlist requires a TOKEN colour inside a
   * `border` shorthand, and the transparent edge keeps a filled primary and an outlined ghost
   * beside it the same height. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  cursor: pointer;
  transition-duration: var(--dur-fast);
  transition-property: filter;
  transition-timing-function: var(--ease);
}

/* The ring is `--accent` here, where the ember's is `--text`. Both follow the same rule for the
 * same reason: a focus ring must contrast against the control it surrounds, and a neutral ring on
 * a neutral fill is the one combination that disappears. */
.ink:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Focusable while refused — a control carrying a reason has to stay in the tab order for the reason
 * to be reachable at all (view ADR §12). */
.ink[aria-disabled='true'],
.ink:disabled {
  color: var(--text-2);
  background: var(--bg-chip);
  border-color: var(--border);
  cursor: not-allowed;
}

.ink:hover:not([aria-disabled='true'], :disabled) {
  filter: var(--cta-press);
}

/* **A primary the app will NOT honour** — present, focusable, and drawn as unavailable.
 *
 * It lives HERE rather than in the page that uses it, because a page's own `.refused` is 0-1-0
 * against this file's `.ink[aria-disabled='true']` at 0-2-0 and loses every property it does not
 * declare at equal specificity — where the winner is then settled by chunk order, which differs
 * between the dev server and the build. `.ink.refused` is 0-2-0 and stands AFTER that block in the
 * same file, so the order is the source's rather than the bundler's.
 *
 * The edge carries meaning — "present but not offered" — so it owes 3:1 against what it stands on.
 * `--border-2` over the row's `--bg-inset` is 1.06:1, an edge that separates nothing. `--text-2` is
 * the ink the WORD already takes here, measuring 6.30:1 on `--bg-inset` in Flame and 8.61:1 in
 * Flame Dark, and one colour for the word and its outline is also what makes the control read as
 * one refused object rather than two greys. */
.ink.refused {
  color: var(--text-2);
  cursor: default;
  background: none;
  border-style: dashed;
  border-color: var(--text-2);
}
